// PDF report builder for the Floor Planner (jsPDF, A4 landscape, one page per room).
import { jsPDF } from "jspdf";
import { fmtEur, fmtM, cm } from "./data";

/** rooms: [{ name, mpp, planAreaM2, image2d, image3d, items }] — image3d typically
 * only for the room that was on screen. Pack totals are computed across ALL rooms
 * and shown on the first page. */
export function exportProjectPdf({ brand, title, packs, rooms }) {
  const b = brand;
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = 297, M = 12;

  // pack usage + investment across every room
  const packTotals = {};
  rooms.forEach((r) => (r.items || []).forEach((it) => { if (it.packId) packTotals[it.packId] = (packTotals[it.packId] || 0) + 1; }));
  const usedPacks = packs.filter((p) => packTotals[p.pack_id]);
  const totalEur = usedPacks.reduce((s, p) => s + (p.price_eur || 0), 0);

  rooms.forEach((room, pageIdx) => {
    if (pageIdx > 0) doc.addPage("a4", "landscape");
    const items = room.items || [];
    let y = 0;

    // Header band
    doc.setFillColor(b.primary);
    doc.rect(0, 0, W, 20, "F");
    doc.setTextColor("#FFFFFF");
    doc.setFont("times", "bold");
    doc.setFontSize(16);
    doc.text(b.name, M, 9);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(b.tagline, M, 15);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Rapport d'aménagement & dimensions", W - M, 10, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(new Date().toLocaleDateString("fr-FR"), W - M, 15.5, { align: "right" });

    y = 26;
    // Meta row
    doc.setTextColor(b.text);
    doc.setFontSize(9);
    const meta = [
      `Projet : ${title || "Sans titre"}${rooms.length > 1 ? ` — ${room.name || `Pièce ${pageIdx + 1}`} (${pageIdx + 1}/${rooms.length})` : ""}`,
      `Surface du plan (est.) : ${room.planAreaM2 ? room.planAreaM2.toFixed(1).replace(".", ",") + " m²" : "—"}`,
      `Échelle : ${room.mpp ? "calibrée (" + fmtM(room.mpp * 100) + " / 100 px)" : "non calibrée"}`,
    ];
    if (pageIdx === 0) {
      meta.push(`Packs : ${usedPacks.length ? usedPacks.map((p) => `${p.name} (${fmtEur(p.price_eur)})`).join("  ·  ") : "aucun"}`);
      meta.push(`Investissement packs : ${usedPacks.length ? fmtEur(totalEur) : "—"}`);
    }
    meta.forEach((line, i) => doc.text(line, M, y + i * 4.6));
    y += meta.length * 4.6 + 4;

    // Images: 2D left, 3D right
    const imgH = 78, imgW = 132;
    if (room.image2d) {
      doc.addImage(room.image2d, "JPEG", M, y, imgW, imgH);
      doc.setFontSize(8); doc.setTextColor(b.muted);
      doc.text("Plan 2D calibré avec mobilier", M, y + imgH + 4);
    }
    if (room.image3d) {
      doc.addImage(room.image3d, "JPEG", M + imgW + 6, y, imgW, imgH);
      doc.text("Vue 3D isométrique", M + imgW + 6, y + imgH + 4);
    }
    y += imgH + 10;

    // Placed items table
    doc.setFontSize(10); doc.setTextColor(b.text); doc.setFont("helvetica", "bold");
    doc.text(`Mobilier placé${rooms.length > 1 ? ` — ${room.name || `Pièce ${pageIdx + 1}`}` : ""}`, M, y); y += 4.5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    if (!items.length) {
      doc.setTextColor(b.muted); doc.text("Aucun meuble placé sur le plan.", M, y); y += 5;
    } else {
      const grouped = {};
      items.forEach((it) => {
        const k = it.name;
        grouped[k] = grouped[k] || { ...it, count: 0 };
        grouped[k].count += 1;
      });
      doc.setFont("helvetica", "bold"); doc.setTextColor(b.muted);
      doc.text("Article", M, y); doc.text("Empreinte (cm)", M + 105, y); doc.text("Haut. (cm)", M + 140, y); doc.text("Qté", M + 165, y);
      doc.setFont("helvetica", "normal"); y += 4;
      Object.values(grouped).forEach((g) => {
        if (y > 185) return;
        doc.setTextColor(b.text);
        doc.text(String(g.name).slice(0, 60), M, y);
        doc.text(`${cm(g.width_m)} × ${cm(g.depth_m)}`, M + 105, y);
        doc.text(String(cm(g.height_m)), M + 140, y);
        doc.text(String(g.count), M + 165, y);
        y += 4;
      });
    }

    // Included-in-pack lists (right column, first page only)
    if (pageIdx === 0 && usedPacks.length) {
      const metaLen = 3 + 2;
      let ry = 26 + metaLen * 4.6 + 4 + imgH + 10;
      const cx = M + 185;
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(b.text);
      doc.text("Également inclus dans le(s) pack(s)", cx, ry); ry += 4.5;
      doc.setFont("helvetica", "normal"); doc.setFontSize(7.2); doc.setTextColor(b.muted);
      usedPacks.forEach((p) => {
        if (ry > 185) return;
        doc.setFont("helvetica", "bold"); doc.text(`${p.name} — ${fmtEur(p.price_eur)}`, cx, ry); ry += 3.8;
        doc.setFont("helvetica", "normal");
        (p.included || []).forEach((inc) => {
          if (ry > 188) return;
          doc.text(`· ${inc}`, cx, ry); ry += 3.2;
        });
        ry += 2;
      });
    }

    // Compliance + CTA footer
    doc.setFillColor(b.subtle);
    doc.rect(0, 192, W, 18, "F");
    doc.setFontSize(7.5); doc.setTextColor(b.muted);
    doc.text("Conformité meublé : les packs couvrent la liste du décret n°2015-981 (logement meublé décent).", M, 198);
    doc.setTextColor(b.primary); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text(`${b.cta} → ${b.ctaUrl}`, M, 205);
    doc.setFont("helvetica", "normal"); doc.setTextColor(b.muted); doc.setFontSize(7.5);
    doc.text(b.footerNote, W - M, 205, { align: "right" });
  });

  doc.save(`plan-${(title || "studio").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`);
}
